import { Palmtree, Plane, Building2, Map, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Palmtree,
    title: "Holiday Packages",
    description: "Curated all-inclusive packages for tropical getaways, beach resorts, and island adventures.",
    image: "https://images.unsplash.com/photo-1668487103999-2779c50c6c0c",
    features: ["All-Inclusive Resorts", "Beach Holidays", "Island Escapes"]
  },
  {
    icon: Plane,
    title: "Flight Bookings",
    description: "Access to premium airlines with competitive rates for domestic and international travel.",
    image: "https://images.pexels.com/photos/804804/pexels-photo-804804.jpeg",
    features: ["Premium Airlines", "Best Price Match", "Flexible Dates"]
  },
  {
    icon: Building2,
    title: "Hotel Reservations",
    description: "From boutique stays to luxury resorts, we find the perfect accommodation for your trip.",
    image: "https://images.pexels.com/photos/297983/pexels-photo-297983.jpeg",
    features: ["Luxury Hotels", "Boutique Stays", "Resort Packages"]
  },
  {
    icon: Map,
    title: "Custom Itineraries",
    description: "Personalized travel plans designed around your preferences, interests, and schedule.",
    image: "https://images.unsplash.com/photo-1646303297330-17073f7823c3",
    features: ["Tailored Plans", "Local Experiences", "Expert Guidance"]
  }
];

export default function Services() {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="services" className="py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-sky-900 mb-4">
            Everything You Need for the Perfect Trip
          </h2>
          <p className="text-sky-600 max-w-2xl mx-auto text-lg">
            From planning to departure, we handle every detail so you can focus on making memories.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="group bg-sky-50 rounded-2xl overflow-hidden border border-sky-100 hover-lift"
              data-testid={`service-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
                  <service.icon className="text-sky-600" size={24} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-sky-900 mb-2">{service.title}</h3>
                <p className="text-sky-600 mb-4 text-sm">{service.description}</p>
                
                {/* Features */}
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-sky-700">
                      <div className="w-1.5 h-1.5 bg-sky-500 rounded-full"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={scrollToContact}
                  className="flex items-center gap-2 text-sky-600 font-medium hover:text-sky-700 transition-colors group/btn"
                  data-testid={`service-learn-more-${index}`}
                >
                  Learn More 
                  <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-sky-700 mb-4">
            Can't find what you're looking for? We can create a custom package just for you.
          </p>
          <Button 
            onClick={scrollToContact}
            className="bg-sky-600 hover:bg-sky-700 text-white px-8"
            data-testid="services-contact-btn"
          >
            Contact Us Today
          </Button>
        </div>
      </div>
    </section>
  );
}
